/* Espace parent/tuteur — liste des enfants liés (roadmap-v1 bloc 03, lot 2B). */

import { useEffect, useState } from 'react';
import { useNavigate } from '../lib/routing';
import { HeaderContent, HEADER_TITLE_CLASS } from '../components/shell/app-header';
import { api } from '../lib/api';
import { Alert } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { CaretRight, UsersFour } from '@phosphor-icons/react';

interface Child {
  classroom_student_id: string;
  first_name: string;
  last_name: string;
  class_label?: string;
}

function Initials({ first_name, last_name }: { first_name: string; last_name: string }) {
  return (
    <div className="shrink-0 w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center font-extrabold text-[15px] select-none">
      {first_name[0]}{last_name[0]}
    </div>
  );
}

export default function ResponsableHome() {
  const navigate = useNavigate();
  const [children, setChildren] = useState<Child[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.parentOrTutor.myChildren
      .query()
      .then((c) => setChildren(c as Child[]))
      .catch(() => setError("La liste de vos enfants n'a pas pu être chargée. Réessayez dans un instant."))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-dvh bg-background flex flex-col">
      <HeaderContent width="md" showBurger>
        <h1 className={HEADER_TITLE_CLASS}>Mes enfants</h1>
      </HeaderContent>
      <div className="max-w-[880px] mx-auto px-4 pt-4 pb-16 flex-1 w-full">

        {loading ? (
          <div className="flex flex-col gap-2">
            {[1, 2].map((i) => <Skeleton key={i} className="h-[68px] rounded-card" />)}
          </div>
        ) : error ? (
          <Alert>{error}</Alert>
        ) : children.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
            <UsersFour size={40} weight="light" className="mb-3 opacity-40" />
            <p className="font-extrabold text-foreground text-[14px]">Aucun enfant lié à votre compte</p>
            <p className="text-[13px] mt-1 opacity-70">Contactez l'établissement si c'est une erreur.</p>
          </div>
        ) : (
          <div className="rounded-card border border-border overflow-hidden shadow-card">
            {children.map((c, i) => (
              <div key={c.classroom_student_id}>
                {i > 0 && <Separator />}
                <button
                  type="button"
                  onClick={() => navigate(`/tutor/enfant/${c.classroom_student_id}`)}
                  className="flex items-center gap-3 w-full px-4 py-3.5 text-left hover:bg-muted/50 transition-colors font-[inherit] bg-transparent border-none cursor-pointer"
                >
                  <Initials first_name={c.first_name} last_name={c.last_name} />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-[14px] text-foreground">
                      {c.first_name} {c.last_name}
                    </div>
                    {c.class_label && (
                      <div className="text-[12px] text-muted-foreground mt-0.5">{c.class_label}</div>
                    )}
                  </div>
                  <CaretRight size={15} className="shrink-0 text-muted-foreground" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
